/**
 * Manage attachments component
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WdOrdersService } from '../../../services/orders/wd-orders.service';
import { WdLocalEnvironmentService } from '../../../services/environment/wd-local-environment.service';
import { WdMediaUploadService } from '../../../services/media/wd-media-upload.service';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IOrderAttachmentModel } from '../../../interfaces/orders/order-interfaces';

@Component({
  selector: 'app-order-attachments-upload',
  templateUrl: './manage-attachments.component.html',
  styleUrls: ['./manage-attachments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ManageAttachmentsComponent implements OnInit {
  // Form
  @ViewChild('attachmentsForm', { static: true })
  public attachmentsForm!: NgForm;

  // Inputs
  orderIndex: string;
  files: Array<IOrderAttachmentModel> = [];
  filesUntouched = '';

  // Booleans
  isChanges = false;
  isLoading = false;
  isPending = false;

  // Callbacks
  onSuccess: any;
  onCancel: any;

  // Count
  filesCount = 0;
  postSize = 0;
  postSizePercentage = 0;

  // Limits
  maxFileSize: number;
  maxFileUploads: number;
  maxFilePostSize: number;

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    public wdFileUploadService: WdMediaUploadService,
    public wdOrdersService: WdOrdersService,
    public dialogRef: MatDialogRef<ManageAttachmentsComponent>,
    public wdEnvironmentService: WdLocalEnvironmentService,
    private wdTranslate: TranslateService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    // Defaults
    this.orderIndex = data.orderIndex;
    this.files = data.files;

    // Booleans
    this.isPending = data.isPending === true;

    // Functions
    this.onSuccess = data.onSuccessCallback;
    this.onCancel = data.onCancelCallback;
    // Limits
    this.maxFileSize = this.wdFileUploadService.maxFileSize;
    this.maxFileUploads = this.wdFileUploadService.maxFileUploads;
    this.maxFilePostSize = this.wdFileUploadService.maxFilePostSize;
  }

  /**
   * On Modal Dismiss
   * @since 4.36.0
   */
  onDismiss(): void {
    // Close the dialog, return false
    this.onCancel();
    this.dialogRef.close(false);
  }

  /**
   * On Init
   * @since 4.36.0
   */
  ngOnInit() {
    this.getDetails();
  }

  /**
   * On Changes to Inputs
   * @since 4.36.0
   */
  onChanges(): void {
    this.isChanges = this.filesUntouched !== JSON.stringify(this.files);
  }

  /**
   * On Modal Remove File
   * @since 4.36.0
   */
  async onRemoveFile(file: any): Promise<void> {
    // Start Loader
    file.isRemoving = true;
    try {
      let response;
      if (!this.isPending) {
        response = await this.wdFileUploadService.removeAttachedMediaFile(
          this.orderIndex,
          file.index,
        );

        // Remove file by name
        this.files = this.files.filter((data: any) => data.index != file.index);
        // Close the dialog, return false
        this.onSuccess(this.files);
        this.dialogRef.close(false);
        // Success Message
        this.wdErrorService.userSuccess(
          this.wdTranslate.instant('labels.success'),
          response.statusText,
        );
      } else {
        // Remove file by name
        this.files = this.files.filter((data: any) => data.name != file.name);
        if (this.files.length === 0) {
          this.onDismiss(); // Close window
        } else {
          this.getDetails(); // Updates progress bar
        }
      }
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      file.isRemoving = false;
    }
  }

  /**
   * On Modal Submit
   * @since 4.36.0
   */
  async onSubmit(form: any): Promise<void> {
    // Start Loader
    this.isLoading = true;
    try {
      let response;
      if (this.isPending) {
        response = await this.wdFileUploadService.uploadPendingFiles(
          this.orderIndex,
          this.files,
          form,
        );
      } else {
        // Currently we limit editing files to one at a time
        response = await this.wdFileUploadService.updateSavedFiles(
          this.orderIndex,
          this.files[0],
        );
      }
      // Close the dialog, return false
      this.onSuccess(this.files);
      this.dialogRef.close(false);
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        response.statusText,
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Get Details
   * @since 4.36.0
   */
  private getDetails() {
    // Set files count
    this.filesCount = this.files.length;

    // Set post size for pending files
    if (this.isPending) {
      this.postSize = this.getPostSize(this.files);
      this.postSizePercentage = (this.postSize / this.maxFilePostSize) * 100;
    } else {
      this.filesUntouched = JSON.stringify(this.files);
    }
  }

  /**
   * Get Post Size from Upload Queue
   * @since 4.36.0
   */
  private getPostSize(files: Array<any>) {
    let result = 0;

    Array.from(files).forEach((file) => {
      result += file.size;
    });

    return result;
  }
}
